import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useState } from 'react';

import Loader from '@/components/common/Loader/Loader';
import LoadingPage from '@/components/common/LoadingPage/LoadingPage';
import { Meta } from '@/layouts/Meta';
import type { GoalBasedQuizRecordModelType } from '@/models/api';
import { initializeStore } from '@/models/root-store';
import type { SeoData } from '@/shared/types';
import { Main } from '@/templates/Main';
import {
  getGeneralPageSeoData,
  getGoalBasedQuizQuestions,
} from '@/utils/api-helpers';

const GoalBase = dynamic(
  () => import('@/components/goalbased/GoalBased/GoalBased'),
  { ssr: false, loading: () => <LoadingPage /> }
);

type GoalBasedProps = {
  goal: string;
  seoData: SeoData;
  goalBasedQuiz: GoalBasedQuizRecordModelType;
};

const GoalMap = {
  caloriecontrol: 'calorie-control',
  musclegain: 'muscle-gain',
  weightloss: 'weight-loss',
};

const Goal: NextPage<GoalBasedProps> = ({ goal, seoData, goalBasedQuiz }) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <>
      {isLoading && <Loader />}
      <Main
        meta={
          <Meta
            title={seoData?.seoTitle || 'Goal Based'}
            description={seoData?.seoDescription || 'Goal Based'}
            canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${
              GoalMap[goal as keyof typeof GoalMap]
            }`}
          />
        }
        seoBlurb={seoData?.seoBlurb}
      >
        <GoalBase
          goal={goal}
          goalBasedQuiz={goalBasedQuiz}
          setIsLoading={setIsLoading}
        />
      </Main>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { goal } = context.params!;
  const store = initializeStore({ ssr: true });
  const quizKey = 'goalbased-quiz';
  const seoData: SeoData = await getGeneralPageSeoData(store, goal as string);
  const goalBasedQuiz: GoalBasedQuizRecordModelType =
    await getGoalBasedQuizQuestions(store, quizKey as string);

  return {
    props: {
      goal,
      seoData,
      goalBasedQuiz,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const goalbasedplans: string[] = [
    'caloriecontrol',
    'musclegain',
    'weightloss',
  ];

  const paths = goalbasedplans.map((goal) => {
    return {
      params: { goal },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export default Goal;
